import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { subscribeToRfqChanges, type RealtimeRfqTable, type RfqChangePayload } from '../api/realtime';
import { queryKeys } from "../api/queryKeys";

/**
 * Options for real-time sync behavior
 */
interface UseRealtimeSyncOptions {
  /**
   * Tables to subscribe to for real-time updates
   * @default ['rfqs', 'rfq_items']
   */
  tables?: RealtimeRfqTable[];
  
  /**
   * Debounce time in milliseconds to batch multiple rapid changes
   * @default 500
   */
  debounceMs?: number;
  
  /**
   * Whether real-time sync is enabled
   * @default true
   */
  enabled?: boolean;
}

/**
 * Hook to enable real-time synchronization of RFQ data across users.
 * 
 * When someone makes changes to quotes or RFQ items, all other users
 * will automatically see the latest data without needing to refresh.
 * This prevents clashing/conflicting quotes when multiple users work
 * on the same RFQs.
 * 
 * Uses Supabase Realtime to listen for INSERT, UPDATE, and DELETE events
 * on the rfqs and rfq_items tables, then invalidates React Query cache
 * to trigger a refetch.
 * 
 * @param options Configuration options for real-time sync
 * @returns Object with connection status and manual refresh function
 */
export function useRealtimeSync(options: UseRealtimeSyncOptions = {}) {
  const {
    tables = ['rfqs', 'rfq_items'],
    debounceMs = 500,
    enabled = true,
  } = options;

  const queryClient = useQueryClient();
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const pendingInvalidationsRef = useRef<Set<string>>(new Set());
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  // Stable key derived from table names so the effect doesn't re-fire
  // when the caller passes a new array instance with the same content.
  const tablesKey = tables.join(',');

  /**
   * Debounced function to invalidate queries.
   * Batches multiple rapid changes to avoid excessive refetches.
   */
  const debouncedInvalidate = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const queriesToInvalidate = Array.from(pendingInvalidationsRef.current);
      pendingInvalidationsRef.current.clear();

      if (queriesToInvalidate.length > 0) {
        // Invalidate all RFQ-related queries
        if (queriesToInvalidate.some(q => q.includes('rfq'))) {
          queryClient.invalidateQueries({ queryKey: queryKeys.rfqs.all });
          queryClient.invalidateQueries({ queryKey: queryKeys.rfqCounts.all });
        }
        // No "updated by another user" toast: postgres_changes events carry no
        // author and `broadcast.self` does not filter them, so the user's own
        // edits came back as someone else's. Nothing here can tell the two apart.
      }
    }, debounceMs);
  }, [queryClient, debounceMs]);

  /**
   * Handle database change events from Supabase Realtime
   */
  const handleChange = useCallback(
    (payload: RfqChangePayload) => {
      const { table } = payload;

      // Add to pending invalidations
      pendingInvalidationsRef.current.add(table);
      
      // Trigger debounced invalidation
      debouncedInvalidate();
    },
    [debouncedInvalidate]
  );

  /**
   * Set up Supabase Realtime subscriptions
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // One channel for both tables (api/realtime.ts). Every change arrives,
    // the user's own included: `broadcast: { self }` applies to broadcast
    // messages only, not to postgres_changes.
    unsubscribeRef.current = subscribeToRfqChanges(tables, handleChange, (status) => {
      if (status === 'SUBSCRIBED') {
        setIsConnected(true);
      } else if (status === 'CHANNEL_ERROR') {
        setIsConnected(false);
        console.error('[RealtimeSync] Failed to connect to real-time updates');
      } else if (status === 'TIMED_OUT') {
        setIsConnected(false);
        console.warn('[RealtimeSync] Connection timed out, will retry');
      }
    });

    // Cleanup on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      
      setIsConnected(false);
    };
  }, [enabled, tablesKey, handleChange]);

  /**
   * Manual refresh function to force a data refetch
   */
  const forceRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.rfqs.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.rfqCounts.all });
  }, [queryClient]);

  return {
    /**
     * Whether real-time sync is currently connected
     */
    isConnected,
    
    /**
     * Manually trigger a data refresh
     */
    forceRefresh,
  };
}
